import React, { FunctionComponent, useCallback, useRef, useState, useEffect } from 'react';
import { Box } from '@material-ui/core';
import clsx from 'clsx';
import CarUnavailable from '../../../../common/assets/images/carUnavailable.png';
import Slider, { Settings } from 'react-slick';
import NextSliderButton from './stockSliderControls/NextSliderButton';
import PrevSliderButton from './stockSliderControls/PrevSliderButton';
import stockSliderStyles from './stockSliderStyles';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import SliderImage from '../../customiseContainer/customiseDisplay/variantSlider/sliderImage/SliderImage';
import SectionStatesWrapper from '../../../../common/components/configuratorStates/SectionStatesWrapper';
import OverlayLoader from '../../../../customise/components/presentational/loaders/OverlayLoader';
import CarCard, { ICarCardProps } from '../CarCard/CarCard';
import useCustomizeCarContext from '../../../hooks/useCustomizeCarContext';
import useSliderIntersectionObserver from '../../../hooks/stockLite/useSliderIntersectionObserver';

export interface IStockSliderSlider {
  elements: ICarCardProps[];
  loading?: boolean;
  handleSelected?: (carId: string) => void;
}

const StockSlider: FunctionComponent<IStockSliderSlider> = ({ elements, loading, handleSelected }) => {
  const style = stockSliderStyles();
  const sliderRef = useRef<Slider>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [minCardHeight, setMinCardHeight] = useState('0');
  const [hideRightArrow, setHideRightArrow] = useState(true);
  const arrowStyles: { [key: string]: string } = {
    default: style.verticalOffset,
    leftArrow: style.leftArrow,
    rightArrow: style.rightArrow,
    hideNext: style.hideNext,
  };

  const { selectedCar } = useCustomizeCarContext();

  const learnMoreLabel = selectedCar?.car?.trim?.attributes?.label ?? '';

  const { observe } = useSliderIntersectionObserver();

  useEffect(() => {
    if (elements?.length && containerRef?.current) {
      const unsubscribe = observe(({ isIntersecting }) => setHideRightArrow(isIntersecting));

      return () => unsubscribe();
    }
  }, [elements, containerRef, loading, currentIndex, observe]);

  const onResize = useCallback(() => {
    if (elements?.length && containerRef?.current) {
      const hasExtendedItems = containerRef.current.querySelector('[data-extended-slide="true"]');
      const containerHeight = containerRef.current.clientHeight;
      if (!hasExtendedItems && containerHeight && containerHeight < 1000) {
        const sliderVerticalPadding = 45;
        setMinCardHeight(`${containerHeight - sliderVerticalPadding}px`);
      }
    }
  }, [elements, currentIndex, containerRef, loading]);

  useEffect(() => {
    const timeoutId = setTimeout(onResize, 100);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [elements, currentIndex, containerRef, loading]);

  useEffect(() => {
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
      setMinCardHeight('0');
    };
  }, [loading]);

  useEffect(() => {
    if (elements.length <= 3) {
      setHideRightArrow(true);
    }
  }, [elements]);

  const updateSlider = useCallback(
    newIndex => {
      sliderRef?.current?.slickGoTo(newIndex);

      return setCurrentIndex(newIndex);
    },
    [currentIndex, elements],
  );

  useEffect(() => {
    if (!loading) {
      sliderRef?.current?.slickGoTo(0);
      setCurrentIndex(0);
    }
  }, [sliderRef, loading]);

  const sliderSettings: Partial<Settings> = {
    dots: false,
    beforeChange: (_oldIndex: number, newIndex: number) => updateSlider(newIndex),
    afterChange: onResize,
    infinite: false,
    arrows: true,
    variableWidth: true,
    className: clsx([style.root, 'stock-slider']),
    prevArrow: <PrevSliderButton customStyles={clsx(arrowStyles.default, arrowStyles.leftArrow)} />,
    nextArrow: (
      <NextSliderButton
        customStyles={clsx(arrowStyles.default, arrowStyles.rightArrow, { [arrowStyles.hideNext]: hideRightArrow })}
      />
    ),
    adaptiveHeight: false,
  };

  return (
    <div ref={containerRef}>
      <Box data-test="stockLite:slider" className={style.sliderContainer}>
        <SectionStatesWrapper>
          <OverlayLoader loading={loading} width="100%">
            <Slider ref={sliderRef} key="stockLiteSlider" {...sliderSettings}>
              {elements.length ? (
                elements.map((props, index) => (
                  <CarCard
                    minHeight={minCardHeight}
                    id={`stockSliderCard_${index}`}
                    learnMoreLabel={learnMoreLabel}
                    {...props}
                    key={index}
                    onTransfer={() => handleSelected(props.carId)}
                  />
                ))
              ) : (
                <SliderImage defaultSrc={CarUnavailable} />
              )}
            </Slider>
          </OverlayLoader>
        </SectionStatesWrapper>
      </Box>
    </div>
  );
};

export default StockSlider;
